<template>
  <div class="ds-page" v-if="!$store.getters.isForbidden">
    <div class="page-header d-print-none">
      <div class="row align-items-center">
        <div class="col">
          <div class="page-pretitle">Administration</div>
          <h2 class="page-title">Internal Users</h2>
          <p class="page-subtitle">Manage administrator access, roles, and account security settings.</p>
        </div>
        <div class="col-auto ms-auto" v-if="currentRole.can_create">
          <button class="ds-btn ds-btn--blue" @click="doShowModalCreate">
            <font-awesome-icon icon="plus" />
            Add Admin
          </button>
        </div>
      </div>
    </div>

    <div class="ds-card">
      <div class="internal-users__toolbar">
        <div>
          <span class="internal-users__toolbar-title">Administrator Directory</span>
          <span class="internal-users__toolbar-subtitle">{{ total_rows }} registered admin{{ total_rows === 1 ? "" : "s" }}</span>
        </div>
        <div class="internal-users__toolbar-actions">
          <el-select v-model="user.search_by" class="internal-users__search-by" size="small" @change="handleSearchByChange">
            <el-option v-for="option in user.search_options" :key="option.value" :label="option.text" :value="option.value" />
          </el-select>
          <el-input
            v-model.trim="user.search_keyword"
            clearable
            class="internal-users__search-input"
            placeholder="Search administrators..."
            size="small"
            @clear="searchHandler"
            @keyup.enter.native="searchHandler"
          />
          <button class="ds-btn ds-btn--primary ds-btn--sm" :disabled="loading.table" @click="searchHandler">
            <font-awesome-icon icon="search" />
            Search
          </button>
          <button class="ds-btn ds-btn--secondary ds-btn--sm" :disabled="loading.table" @click="refreshList">
            <i class="el-icon-refresh"></i>
            Refresh
          </button>
        </div>
      </div>

      <div class="ds-table-wrap">
        <table class="ds-table" v-loading="loading.table">
          <thead>
            <tr>
              <th>Administrator</th>
              <th>Role</th>
              <th class="text-center">Is Head Role</th>
              <th class="text-center">Two-factor Auth</th>
              <th>Created Date</th>
              <th>Last Login Date</th>
              <th>Status</th>
              <th class="text-center">Action</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in userList" :key="item.id">
              <td>
                <div class="ds-user-cell">
                  <div class="ds-avatar" :class="dsAvatarVariant(item.name)">{{ dsGetInitials(item.name) }}</div>
                  <div>
                    <div class="ds-user-cell__name">{{ item.name || "—" }}</div>
                    <div class="ds-user-cell__sub">{{ item.email || "—" }}</div>
                  </div>
                </div>
              </td>
              <td>
                <span class="internal-users__role">{{ item.role?.name || "—" }}</span>
              </td>
              <td class="text-center">
                <span class="ds-pill" :class="item.is_head_role ? 'ds-pill--success' : 'ds-pill--gray'">
                  {{ item.is_head_role ? "Yes" : "No" }}
                </span>
              </td>
              <td class="text-center">
                <span class="ds-pill" :class="item.tfa_active ? 'ds-pill--success' : 'ds-pill--gray'">
                  {{ item.tfa_active ? "Active" : "Inactive" }}
                </span>
              </td>
              <td>
                <span class="internal-users__date">{{ item.created_at_str }}</span>
              </td>
              <td>
                <span class="internal-users__date">{{ item.last_login_at_str }}</span>
              </td>
              <td>
                <span class="ds-pill" :class="userStatusClass(item.status)">{{ item.status_str }}</span>
              </td>
              <td class="text-center">
                <el-dropdown
                  trigger="hover"
                  @command="handleActionCommand($event, item)"
                  popper-class="internal-users__action-popper"
                >
                  <button class="ds-btn ds-btn--secondary ds-btn--sm internal-users__action-trigger">
                    Actions <i class="el-icon-arrow-down el-icon--right"></i>
                  </button>
                  <el-dropdown-menu slot="dropdown" class="internal-users__action-menu">
                    <el-dropdown-item command="edit">
                      <font-awesome-icon icon="pen" class="text-primary" /> Edit Admin
                    </el-dropdown-item>
                    <el-dropdown-item v-if="item.id !== $store.getters.currentMyProfile.id" command="delete" divided>
                      <font-awesome-icon icon="trash" class="text-danger" /> Delete Admin
                    </el-dropdown-item>
                    <el-dropdown-item v-if="role === 'Super Admin'" command="reset-tfa">
                      <font-awesome-icon icon="shield-alt" class="text-warning" /> Reset TFA
                    </el-dropdown-item>
                  </el-dropdown-menu>
                </el-dropdown>
              </td>
            </tr>
          </tbody>
        </table>
        <el-empty description="No data found" v-if="total_rows == 0" :image="emptyStateImage" class="ds-empty"></el-empty>
      </div>
      <pagination :page="page" :per_page="per_page" :list="userList" :total_rows="total_rows" @pageChange="pageChangeHandler" />
    </div>

    <b-modal v-model="showModalCreate" :title="modalTitle" hide-footer>
      <div class="internal-users__modal-intro">
        {{
          modalType === "add"
            ? "Create an administrator account and assign access permissions."
            : "Update this administrator account and access permissions."
        }}
      </div>
      <b-form @submit.prevent="submitAdmin" @reset="closeModalCreate">
        <b-form-group label="Admin Name">
          <b-input v-model="v$.form.name.$model" placeholder="Input admin name" type="text" />
          <b-form-invalid-feedback :state="!v$.form.name.$invalid" v-if="v$.form.name.$errors[0]">
            <span> {{ v$.form.name.$errors[0].$message }}</span>
          </b-form-invalid-feedback>
        </b-form-group>
        <b-form-group label="Admin Email">
          <b-input v-model="v$.form.email.$model" placeholder="Input admin email" type="text" />
          <b-form-invalid-feedback :state="!v$.form.email.$invalid" v-if="v$.form.email.$errors[0]">
            <span> {{ v$.form.email.$errors[0].$message }}</span>
          </b-form-invalid-feedback>
        </b-form-group>
        <b-form-group label="Admin Password">
          <b-input-group>
            <template #append>
              <b-input-group-text class="cursor-pointer" @click="handlerClickGeneratePassword">
                <span><font-awesome-icon icon="cogs" /></span>
              </b-input-group-text>
            </template>
            <b-input
              :disabled="modalType == 'add'"
              v-model="v$.form.password.$model"
              placeholder="Input admin password."
              type="text"
            />
          </b-input-group>
          <small v-if="modalType === 'edit'" class="text-brand">
            <i>(Leave blank if you won't update admin password)</i>
          </small>
          <b-form-invalid-feedback :state="!v$.form.password.$invalid" v-if="v$.form.password.$errors[0]">
            <span> {{ v$.form.password.$errors[0].$message }}</span>
          </b-form-invalid-feedback>
        </b-form-group>
        <b-form-group label="Role">
          <el-select @change="onChangeRole" v-model="v$.form.role_id.$model" class="w-full" placeholder="Select admin role">
            <el-option v-for="item in roleList" :key="item.id" :value="item.id" :label="item.name" />
          </el-select>
          <b-form-invalid-feedback :state="!v$.form.role_id.$invalid" v-if="v$.form.role_id.$errors[0]">
            <span> {{ v$.form.role_id.$errors[0].$message }}</span>
          </b-form-invalid-feedback>
        </b-form-group>
        <b-form-group v-if="modalType === 'edit'" label="Is Head Role">
          <el-switch
            :active-value="true"
            :inactive-value="false"
            active-text="Yes"
            inactive-text="No"
            v-model="form.is_head_role"
          >
          </el-switch>
        </b-form-group>
        <b-form-group v-if="[finance_role_id, crm_role_id].includes(form.role_id)" label="Is Maker">
          <el-switch :active-value="true" :inactive-value="false" active-text="Yes" inactive-text="No" v-model="form.is_maker">
          </el-switch>
        </b-form-group>
        <b-form-group v-if="[finance_role_id, crm_role_id].includes(form.role_id)" label="Is Checker">
          <el-switch :active-value="true" :inactive-value="false" active-text="Yes" inactive-text="No" v-model="form.is_checker">
          </el-switch>
        </b-form-group>
        <div class="internal-users__modal-actions" v-if="currentRole['can_' + (modalType == 'add' ? 'create' : modalType)]">
          <button type="button" class="ds-btn ds-btn--secondary ds-btn--sm" @click="closeModalCreate">Cancel</button>
          <button type="submit" class="ds-btn ds-btn--primary ds-btn--sm" :disabled="loading.form_admin">
            {{ loading.form_admin ? "Saving..." : "Submit" }}
          </button>
        </div>
      </b-form>
    </b-modal>
  </div>
</template>
<script>
import useVuelidate from "@vuelidate/core";
import crypto from "crypto";
import { designSystemMixin } from "@/mixins/designSystemMixin";
import { required, email, requiredIf, helpers } from "@vuelidate/validators";
import { RESET_ACTIVATE, MY_PROFILE } from "@/store/modules/auth";
import {
  GET_INTERNALUSERS,
  CREATE_INTERNALUSERS,
  UPDATE_INTERNALUSERS,
  DELETE_INTERNALUSERS,
} from "@/store/modules/internal_users";
import { GET_ALL_ROLES } from "@/store/modules/roles";
import emptyStateImage from "@/assets/images/empty-state.png";
import popupErrorMessages from "@/library/popup-error-messages";
import base64RSA from "@/library/rsa-encrypt";
import passwordComplexity from "@/library/password-complexity";
import Pagination from "../components/Pagination.vue";

export default {
  name: "Admin",
  mixins: [designSystemMixin],
  components: { Pagination },
  metaInfo: {
    title: "Admin",
  },
  setup() {
    return {
      v$: useVuelidate(),
    };
  },
  data() {
    return {
      emptyStateImage,
      showModalCreate: false,
      loading: {
        form_admin: false,
        table: false,
      },
      modalType: "add",
      user: {
        search_by: "name",
        search_keyword: "",
        search_options: [
          { value: "name", text: "Name" },
          { value: "email", text: "Email" },
          { value: "role", text: "Role" },
        ],
      },
      role: "",
      per_page: 10,
      total_rows: 0,
      page: 1,
      userList: [],
      roleList: [],
      form: {
        name: "",
        email: "",
        password: "",
        role_id: "",
        is_maker: false,
        is_checker: false,
        is_head_role: false,
      },
      selectedRole: {},
      finance_role_id: process.env.VUE_APP_FINANCE_ROLE_ID,
      crm_role_id: process.env.VUE_APP_CRM_ROLE_ID,
    };
  },
  validations() {
    return {
      form: {
        name: { required },
        email: { required, email },
        password: {
          required: requiredIf(this.modalType === "add"),
          complexity: helpers.withMessage(
            () => "Password should have at least 1 uppercase, 1 lowercase, 1 number and 1 special character",
            passwordComplexity
          ),
        },
        role_id: { required },
        is_maker: { required: requiredIf(this.form.role_id === this.finance_role_id) },
        is_checker: { required: requiredIf(this.form.role_id === this.finance_role_id) },
      },
    };
  },
  computed: {
    currentRole() {
      return this.$store.getters.current_role;
    },
    modalTitle() {
      return this.modalType === "add" ? "Create New Admin" : "Edit Admin";
    },
  },
  async mounted() {
    const loader = this.$loading.show();
    await this.getList();
    await this.getRoleList();
    this.loadProfile();
    loader.hide();
  },
  methods: {
    userStatusClass(status) {
      if (status === "not_active") return "ds-pill--gray";
      return this.dsStatusClass(status);
    },
    handleActionCommand(command, item) {
      if (command === "edit") this.doShowModalEdit(item);
      if (command === "delete") this.confirmDeleteAdmin(item);
      if (command === "reset-tfa") this.confirmResetTfa(item);
    },
    async refreshList() {
      this.loading.table = true;
      await this.getList();
      this.loading.table = false;
    },
    handleSearchByChange() {
      this.user.search_keyword = "";
      this.searchHandler();
    },
    async searchHandler() {
      this.page = 1;
      this.loading.table = true;
      await this.getList();
      this.loading.table = false;
    },
    generateRandomPassword() {
      const wishlist = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz~!@-#$";
      const length = 10;
      return Array.from(crypto.randomFillSync(new Uint8Array(length)))
        .map((x) => wishlist[x % wishlist.length])
        .join("");
    },
    async getList() {
      await this.$store
        .dispatch(GET_INTERNALUSERS, {
          page: this.page,
          keyword: this.user.search_keyword,
          search_by: this.user.search_by,
        })
        .then((response) => {
          this.userList = response.data.rows;
          this.total_rows = response.data.count;
        })
        .catch(() => {});
    },
    async getRoleList() {
      await this.$store
        .dispatch(GET_ALL_ROLES)
        .then((response) => {
          this.roleList = response.rows;
        })
        .catch(() => {});
    },
    onChangeRole(value) {
      this.selectedRole = this.roleList.find((v) => v.id === value);
    },
    async pageChangeHandler(page) {
      this.page = page;
      this.loading.table = true;
      await this.getList();
      this.loading.table = false;
    },
    doShowModalCreate() {
      this.showModalCreate = true;
      this.modalType = "add";
      this.form = {
        name: "",
        email: "",
        password: this.generateRandomPassword(),
        role_id: "",
        is_maker: false,
        is_checker: false,
      };
      this.v$.form.$touch();
    },
    doShowModalEdit(item) {
      this.showModalCreate = true;
      this.modalType = "edit";
      /* eslint-disable prefer-object-spread */
      this.form = Object.assign(
        { password: "" },
        {
          name: item.name,
          email: item.email,
          role_id: item.role.id,
          id: item.id,
          is_maker: item.is_maker,
          is_checker: item.is_checker,
          is_head_role: item.is_head_role,
        }
      );
      this.v$.form.$touch();
    },
    closeModalCreate() {
      this.showModalCreate = false;
      this.form = {
        name: "",
        email: "",
        password: "",
        role_id: "",
        is_maker: false,
        is_checker: false,
      };
    },
    submitAdmin() {
      if (this.modalType === "add") {
        this.createAdmin();
      } else {
        this.updateAdmin();
      }
    },
    createAdmin() {
      this.v$.form.$touch();
      if (this.v$.form.$error) return;

      this.$confirm(`You will create new admin with role as a ${this.selectedRole.name}. Continue?`, "Confirmation", {
        type: "info",
        beforeClose: async (action, instance, done) => {
          if (action === "confirm") {
            done();
            /* eslint-disable prefer-object-spread */
            const obj = Object.assign({}, this.form);
            obj.password = base64RSA.encrypt(obj.password).toString("base64");
            this.loading.form_admin = true;
            await this.$store
              .dispatch(CREATE_INTERNALUSERS, obj)
              .then(() => {
                this.showModalCreate = false;
                this.$message({
                  title: "Success",
                  type: "success",
                  message: "New admin created",
                });
                this.getList();
              })
              .catch((err) => {
                popupErrorMessages(err.response.data);
              });
            this.loading.form_admin = false;
            return;
          }
          done();
        },
      }).catch(() => {});
    },
    updateAdmin() {
      this.v$.form.$touch();
      if (this.v$.form.$error) {
        if (this.v$.form.$errors.length === 1 && !this.form.password && this.v$.form.$errors[0].$validator === "complexity") {
          // skip
        } else {
          return;
        }
      }

      this.$confirm("You will update this admin data. Continue?", "Confirmation", {
        type: "info",
        beforeClose: async (action, instance, done) => {
          if (action === "confirm") {
            done();
            /* eslint-disable prefer-object-spread */
            const obj = Object.assign({}, this.form);
            this.loading.form_admin = true;
            await this.$store
              .dispatch(UPDATE_INTERNALUSERS, {
                id: obj.id,
                data: {
                  email: obj.email,
                  password: obj.password ? base64RSA.encrypt(obj.password).toString("base64") : null,
                  role_id: obj.role_id,
                  name: obj.name,
                  is_maker: obj.is_maker,
                  is_checker: obj.is_checker,
                  is_head_role: obj.is_head_role,
                },
              })
              .then(() => {
                this.showModalCreate = false;
                this.$message({
                  title: "Success",
                  type: "success",
                  message: "Admin data updated",
                });
                this.getList();
              })
              .catch((err) => {
                popupErrorMessages(err.response.data);
              });
            this.loading.form_admin = false;
            return;
          }
          done();
        },
      }).catch(() => {});
    },
    confirmDeleteAdmin(item) {
      this.$confirm(`You will remove ${item.name} from Admin user. Continue?`, "Confirmation", {
        type: "info",
        beforeClose: async (action, instance, done) => {
          if (action === "confirm") {
            instance.confirmButtonLoading = true;
            await this.$store
              .dispatch(DELETE_INTERNALUSERS, {
                id: item.id,
              })
              .then(() => {
                this.showModalCreate = false;
                this.$message({
                  title: "Success",
                  type: "success",
                  message: "Admin deleted",
                });
                this.getList();
                done();
              })
              .catch((err) => {
                popupErrorMessages(err.response.data);
              });
            instance.confirmButtonLoading = false;
            return;
          }
          done();
        },
      }).catch(() => {});
    },
    confirmResetTfa(item) {
      this.$confirm("You will Reset this Admin Tfa. Continue?", "Confirmation", {
        type: "info",
        beforeClose: async (action, instance, done) => {
          if (action === "confirm") {
            instance.confirmButtonLoading = true;
            await this.$store
              .dispatch(RESET_ACTIVATE, {
                id: item.id,
              })
              .then(() => {
                this.showModalCreate = false;
                this.$message({
                  title: "Success",
                  type: "success",
                  message: "Tfa Deactivated",
                });
                this.getList();
                done();
              })
              .catch((err) => {
                popupErrorMessages(err.response.data);
              });
            instance.confirmButtonLoading = false;
            return;
          }
          done();
        },
      }).catch(() => {});
    },
    async loadProfile() {
      try {
        const response = await this.$store.dispatch(MY_PROFILE);
        this.role = response?.me?.role?.name || response?.data?.me?.role?.name || response?.data?.data?.me?.role?.name;
      } catch (error) {
        console.error("Error load profile:", error);
      }
    },
    handlerClickGeneratePassword() {
      try {
        this.form.password = this.generateRandomPassword();
        this.v$.form.$touch();
      } catch (error) {
        console.log(error);
      }
    },
  },
};
</script>

<style scoped>
.internal-users__toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 14px 20px;
  border-bottom: 1px solid rgba(101, 109, 119, 0.16);
}
.internal-users__toolbar-title,
.ds-user-cell__name {
  display: block;
  color: #232e3c;
  font-size: 13.5px;
  font-weight: 700;
}
.internal-users__toolbar-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}
.internal-users__search-by {
  width: 110px;
}
.internal-users__search-input {
  width: 220px;
}
.internal-users__toolbar-subtitle,
.ds-user-cell__sub,
.internal-users__date {
  color: #656d77;
  font-size: 12px;
}
.ds-user-cell {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 220px;
}
.ds-user-cell__sub {
  margin-top: 2px;
}
.internal-users__role {
  color: #49566a;
  font-weight: 600;
}
.internal-users__action-trigger {
  min-width: 86px;
}
.internal-users__modal-intro {
  margin-bottom: 20px;
  color: #656d77;
  font-size: 13px;
}
.internal-users__modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 24px;
}
@media (max-width: 767.98px) {
  .internal-users__toolbar {
    align-items: flex-start;
    flex-direction: column;
  }
  .internal-users__toolbar-subtitle {
    display: block;
    margin-top: 3px;
  }
  .internal-users__toolbar-actions {
    width: 100%;
    flex-wrap: wrap;
  }
  .internal-users__search-input {
    flex: 1;
    min-width: 180px;
  }
}
</style>
